import { Fragment, type ReactElement, type ReactNode } from 'react';

import type { SxProps, Theme } from '@mui/material/styles';

import { scrollbar } from '@bestseller-bit/frontend-community.theme.buying-theme';
import Box from '@mui/material/Box';

import { useVirtualRows } from '../../../hooks/PurchaseOrdersHooks/useVirtualRows';

import { horizontalShadowOverlay, verticalShadowStrips } from './scrollShadows';
import { useScrollShadows } from './useScrollShadows';

interface VirtualTableRow {
    /** Stable identity for the row, used as its React key. */
    key: string;
    /** Which row variant this is; looked up in `rowHeights` to size the row. */
    kind: string;
}

interface VirtualTableScrollerProps<TRow extends VirtualTableRow> {
    /** Placement for the scroller, spanning the header and body row tracks of the parent grid. */
    sx: Exclude<SxProps<Theme>, ReadonlyArray<unknown>>;
    /** The sticky header row; rendered as the first child so it pins to the top while rows scroll. */
    header: ReactNode;
    /** The flat row list to render, including the rows scrolled out of view. */
    rows: ReadonlyArray<TRow>;
    /** Pixel height for every row variant, needed up front so rows can be positioned unrendered. */
    rowHeights: Record<TRow['kind'], number>;
    /**
     * Per-row height override for rows whose size can't be known from `kind` alone
     * (e.g. a row that grows to show an error line). Falls back to `rowHeights[kind]`.
     */
    getRowHeight?: (row: TRow) => number;
    /** Renders a row. The index is only needed for position hints such as `aria-posinset`. */
    renderRow: (row: TRow, index: number) => ReactNode;
    /** Scrolls back to the top whenever this changes, for example a search term or sort. */
    resetKey?: string;
    /** Rows rendered beyond the visible window on each side; higher trades memory for fewer blank flashes on fast scroll. */
    overscan?: number;
    /** Rendered in place of the rows when there are none, keeping the header visible. */
    emptyState?: ReactNode;
}

// Shadow values match the shared ScrollBox so virtualised and plain scroll areas look the same.
/**
 * A virtualised table body with a sticky header and scroll shadows on each overflowing edge.
 *
 * Header and rows share one scroll container, so their columns cannot drift apart, and the
 * container adopts its parent grid's row tracks via `subgrid` so the header stays aligned with
 * whatever else sits in that row. Only the visible rows are rendered; the rest are stood in for
 * by two spacers, keeping the rows in normal flow so they remain free to size horizontally.
 */
const VirtualTableScroller = <TRow extends VirtualTableRow>({
    sx,
    header,
    rows,
    rowHeights,
    getRowHeight,
    renderRow,
    resetKey,
    overscan,
    emptyState,
}: VirtualTableScrollerProps<TRow>): ReactElement => {
    const { scrollRef, virtualRows, topSpacerHeight, bottomSpacerHeight } =
        useVirtualRows({
            rowCount: rows.length,
            getRowHeight: (index): number => {
                const row = rows[index];
                // Per-row override wins; otherwise size by kind.
                return getRowHeight
                    ? getRowHeight(row)
                    : rowHeights[row.kind as TRow['kind']];
            },
            getRowKey: (index): string => rows[index].key,
            resetKey,
            overscan,
        });

    const rowCount = rows.length;

    const { verticalShadowRef, horizontalShadowRef } = useScrollShadows({
        scrollRef,
        rowCount,
    });

    return (
        <>
            <Box
                ref={scrollRef}
                sx={[
                    {
                        display: 'grid',
                        // Adopts the parent grid's header and body row tracks.
                        gridTemplateRows: 'subgrid',
                        ...scrollbar(),
                        overflow: 'auto',
                    },
                    sx,
                ]}
            >
                {header}

                {rowCount === 0 && emptyState}

                {rowCount > 0 && (
                    <Box
                        ref={verticalShadowRef}
                        sx={{
                            gridRow: 2,
                            minWidth: '100%',
                            minHeight: 'fit-content',
                            ...verticalShadowStrips,
                        }}
                    >
                        <Box sx={{ height: topSpacerHeight }} />

                        {virtualRows.map((virtualRow) => (
                            <Fragment key={virtualRow.key}>
                                {renderRow(
                                    rows[virtualRow.index],
                                    virtualRow.index
                                )}
                            </Fragment>
                        ))}

                        <Box sx={{ height: bottomSpacerHeight }} />
                    </Box>
                )}
            </Box>

            {/* Outside the scroller so the strips hold the viewport edges and cross the header. */}
            <Box
                ref={horizontalShadowRef}
                sx={[horizontalShadowOverlay, sx]}
            />
        </>
    );
};

export { VirtualTableScroller };
export type { VirtualTableRow, VirtualTableScrollerProps };
